import React, { useState, useEffect, useMemo } from 'react';
import { 
  Play, Plus, Trash2, Edit2, CheckCircle2, 
  ExternalLink, Eye, EyeOff, Save, RefreshCw, LogOut, 
  Briefcase, MessageSquare, Award, X, Download, Search, Reply,
  ShieldAlert, Globe
} from 'lucide-react';
import { CaseStudy, PerformanceRecording, Credential, ContactInquiry } from '../types';

interface AdminDashboardProps {
  token: string;
  adminUser: { email: string; name: string };
  onLogout: () => void;
  onViewLiveSite: () => void;
  initialRecordings?: PerformanceRecording[];
  initialCaseStudies?: CaseStudy[];
  initialCredentials?: Credential[];
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({
  token,
  adminUser,
  onLogout,
  onViewLiveSite,
  initialRecordings = [],
  initialCaseStudies = [],
  initialCredentials = [],
}) => {
  const [activeTab, setActiveTab] = useState<'recordings' | 'casestudies' | 'credentials' | 'inquiries'>('recordings');
  const [recordings, setRecordings] = useState<PerformanceRecording[]>(initialRecordings);
  const [caseStudies, setCaseStudies] = useState<CaseStudy[]>(initialCaseStudies);
  const [credentials, setCredentials] = useState<Credential[]>(initialCredentials);
  const [inquiries, setInquiries] = useState<ContactInquiry[]>([]);
  const [inquirySearch, setInquirySearch] = useState<string>('');
  const [inquiryFilter, setInquiryFilter] = useState<'all' | 'valid' | 'spam'>('all');
  const [loading, setLoading] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<string>('');

  // Editing modals / state
  const [editingRecording, setEditingRecording] = useState<PerformanceRecording | null>(null);
  const [editingCaseStudy, setEditingCaseStudy] = useState<CaseStudy | null>(null);
  const [editingCredential, setEditingCredential] = useState<Credential | null>(null);

  const authHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  const showFeedback = (msg: string) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(''), 4000);
  };

  const downloadDatabaseBackup = () => {
    const backupData = {
      exported_at: new Date().toISOString(),
      recordings,
      case_studies: caseStudies,
      credentials,
      inquiries,
    };
    const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tdesrosi-portfolio-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showFeedback('Database backup downloaded successfully!');
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [recRes, csRes, credRes, inqRes] = await Promise.allSettled([
        fetch('/api/admin/recordings', { headers: authHeaders }).then((r) => r.ok ? r.json() : null),
        fetch('/api/admin/case-studies', { headers: authHeaders }).then((r) => r.ok ? r.json() : null),
        fetch('/api/admin/credentials', { headers: authHeaders }).then((r) => r.ok ? r.json() : null),
        fetch('/api/admin/inquiries', { headers: authHeaders }).then((r) => r.ok ? r.json() : null),
      ]);

      if (recRes.status === 'fulfilled' && Array.isArray(recRes.value)) {
        setRecordings(recRes.value);
      }
      if (csRes.status === 'fulfilled' && Array.isArray(csRes.value)) {
        setCaseStudies(csRes.value);
      }
      if (credRes.status === 'fulfilled' && Array.isArray(credRes.value)) {
        setCredentials(credRes.value);
      }
      if (inqRes.status === 'fulfilled' && Array.isArray(inqRes.value)) {
        setInquiries(inqRes.value);
      }
    } catch (e) {
      console.error('Failed to load admin data:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // --- Toggle Published State Handlers ---
  const toggleRecordingPublished = async (rec: PerformanceRecording) => {
    const updated = { ...rec, published: !rec.published };
    try {
      const res = await fetch('/api/admin/recordings', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(updated),
      });
      if (res.ok) {
        setRecordings((prev) => prev.map((r) => (r.id === rec.id ? updated : r)));
        showFeedback(`Recording "${rec.title}" is now ${updated.published ? 'Published' : 'Draft'}.`);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const toggleCaseStudyPublished = async (study: CaseStudy) => {
    const updated = { ...study, published: !study.published };
    try {
      const res = await fetch('/api/admin/case-studies', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(updated),
      });
      if (res.ok) {
        setCaseStudies((prev) => prev.map((s) => (s.id === study.id ? updated : s)));
        showFeedback(`Case study "${study.title}" is now ${updated.published ? 'Published' : 'Draft'}.`);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const toggleCredentialPublished = async (cred: Credential) => {
    const updated = { ...cred, published: !cred.published };
    try {
      const res = await fetch('/api/admin/credentials', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(updated),
      });
      if (res.ok) {
        setCredentials((prev) => prev.map((c) => (c.title === cred.title && c.institution === cred.institution ? updated : c)));
        showFeedback(`Credential "${cred.title}" is now ${updated.published ? 'Published' : 'Draft'}.`);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // --- Deletion Handlers ---
  const deleteRecording = async (id: string) => {
    if (!confirm('Are you sure you want to delete this recording?')) return;
    try {
      const res = await fetch(`/api/admin/recordings/${id}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (res.ok) {
        setRecordings((prev) => prev.filter((r) => r.id !== id));
        showFeedback('Recording deleted.');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const deleteCaseStudy = async (id: string) => {
    if (!confirm('Are you sure you want to delete this case study?')) return;
    try {
      const res = await fetch(`/api/admin/case-studies/${id}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (res.ok) {
        setCaseStudies((prev) => prev.filter((s) => s.id !== id));
        showFeedback('Case study deleted.');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const deleteCredential = async (title: string, institution: string) => {
    if (!confirm(`Are you sure you want to delete "${title}"?`)) return;
    try {
      const res = await fetch(`/api/admin/credentials?title=${encodeURIComponent(title)}&institution=${encodeURIComponent(institution)}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (res.ok) {
        setCredentials((prev) => prev.filter((c) => !(c.title === title && c.institution === institution)));
        showFeedback('Credential deleted.');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const deleteInquiry = async (id: string, name: string) => {
    if (!confirm(`Delete contact inquiry from "${name}"?`)) return;
    try {
      const res = await fetch(`/api/admin/inquiries/${id}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (res.ok) {
        setInquiries((prev) => prev.filter((i) => i.id !== id));
        showFeedback('Inquiry deleted.');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const spamCount = useMemo(() => inquiries.filter((i) => i.is_spam).length, [inquiries]);
  const validCount = useMemo(() => inquiries.filter((i) => !i.is_spam).length, [inquiries]);

  const filteredInquiries = useMemo(() => {
    let list = inquiries;
    if (inquiryFilter === 'valid') {
      list = list.filter((inq) => !inq.is_spam);
    } else if (inquiryFilter === 'spam') {
      list = list.filter((inq) => inq.is_spam);
    }
    if (!inquirySearch.trim()) return list;
    const query = inquirySearch.toLowerCase();
    return list.filter((inq) => 
      inq.name.toLowerCase().includes(query) ||
      inq.email.toLowerCase().includes(query) ||
      inq.subject.toLowerCase().includes(query) ||
      inq.message.toLowerCase().includes(query) ||
      (inq.ip_address && inq.ip_address.toLowerCase().includes(query)) ||
      (inq.spam_reason && inq.spam_reason.toLowerCase().includes(query))
    );
  }, [inquiries, inquirySearch, inquiryFilter]);

  // --- Save Handlers ---
  const saveRecording = async (rec: PerformanceRecording) => {
    try {
      const res = await fetch('/api/admin/recordings', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(rec),
      });
      if (res.ok) {
        showFeedback('Recording saved successfully!');
        setEditingRecording(null);
        fetchAllData();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const saveCaseStudy = async (study: CaseStudy) => {
    try {
      const res = await fetch('/api/admin/case-studies', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(study),
      });
      if (res.ok) {
        showFeedback('Case study saved successfully!');
        setEditingCaseStudy(null);
        fetchAllData();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const saveCredential = async (cred: Credential) => {
    try {
      const res = await fetch('/api/admin/credentials', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(cred),
      });
      if (res.ok) {
        showFeedback('Credential saved successfully!');
        setEditingCredential(null);
        fetchAllData();
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
      {/* Top Navbar */}
      <header style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', position: 'sticky', top: 0, zIndex: 100 }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '4rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontWeight: 700, fontFamily: 'var(--font-display)', fontSize: '1.2rem' }}>
              tdesrosi.com
            </span>
            <span style={{ padding: '0.2rem 0.6rem', borderRadius: 'var(--radius-full)', background: 'rgba(212, 175, 55, 0.15)', color: 'var(--accent-gold)', fontSize: '0.75rem', fontWeight: 600 }}>
              Admin Control Plane
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Logged in: <strong style={{ color: 'var(--text-primary)' }}>{adminUser.email}</strong>
            </span>
            <button 
              onClick={downloadDatabaseBackup} 
              className="btn btn-secondary" 
              style={{ padding: '0.4rem 0.9rem', fontSize: '0.85rem' }}
              title="Download complete datastore snapshot as JSON"
            >
              <Download size={14} />
              <span>Export JSON</span>
            </button>
            <button onClick={onViewLiveSite} className="btn btn-secondary" style={{ padding: '0.4rem 0.9rem', fontSize: '0.85rem' }}>
              <span>View Live Site</span>
              <ExternalLink size={14} />
            </button>
            <button onClick={onLogout} className="btn btn-secondary" style={{ padding: '0.4rem 0.9rem', fontSize: '0.85rem', color: '#f87171' }}>
              <LogOut size={14} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <div className="container" style={{ padding: '2rem 1.5rem 6rem' }}>
        {feedback && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.25rem', borderRadius: 'var(--radius-md)', background: 'rgba(16, 185, 129, 0.15)', color: 'var(--accent-emerald)', marginBottom: '1.5rem', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
            <CheckCircle2 size={18} />
            <span>{feedback}</span>
          </div>
        )}

        {/* Tab Navigation */}
        <div style={{ display: 'flex', gap: '0.5rem', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setActiveTab('recordings')}
            className={`btn ${activeTab === 'recordings' ? 'btn-primary' : 'btn-secondary'}`}
          >
            <Play size={16} />
            <span>Performance Recordings ({recordings.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('casestudies')}
            className={`btn ${activeTab === 'casestudies' ? 'btn-primary' : 'btn-secondary'}`}
          >
            <Briefcase size={16} />
            <span>Case Studies & Work ({caseStudies.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('credentials')}
            className={`btn ${activeTab === 'credentials' ? 'btn-primary' : 'btn-secondary'}`}
          >
            <Award size={16} />
            <span>Background & Credentials ({credentials.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('inquiries')}
            className={`btn ${activeTab === 'inquiries' ? 'btn-primary' : 'btn-secondary'}`}
          >
            <MessageSquare size={16} />
            <span>Inquiries Inbox ({inquiries.length})</span>
          </button>
          <button
            onClick={fetchAllData}
            className="btn btn-secondary"
            title="Refresh All"
            style={{ marginLeft: 'auto' }}
          >
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
          </button>
        </div>

        {/* TAB 1: RECORDINGS */}
        {activeTab === 'recordings' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h3 style={{ fontSize: '1.35rem', fontWeight: 700 }}>Recital & Concert Recordings</h3>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Add new YouTube performances, edit collaborator credits and venue metadata.</p>
              </div>
              <button
                onClick={() => setEditingRecording({
                  id: `rec_${Date.now()}`,
                  title: '',
                  composer: '',
                  piece: '',
                  movement: '',
                  instrumentation: 'Clarinet and Piano',
                  event: 'Clarinet Recital',
                  date: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
                  venue: 'Atlanta, GA',
                  youtube_url: '',
                  youtube_id: '',
                  performers: [
                    { name: 'Thomas Desrosiers', instrument: 'Clarinet', affiliation: 'Classical Remix', url: 'https://tdesrosi.com' }
                  ],
                  description: '',
                  published: true
                })}
                className="btn btn-primary"
              >
                <Plus size={16} />
                <span>Add New Recording</span>
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '1.5rem' }}>
              {recordings.map((rec) => (
                <div key={rec.id} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '1.25rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ position: 'relative', marginBottom: '0.75rem', borderRadius: 'var(--radius-sm)', overflow: 'hidden', background: '#000' }}>
                      <img 
                        src={`https://i.ytimg.com/vi/${rec.youtube_id}/hqdefault.jpg`} 
                        alt={rec.title} 
                        style={{ width: '100%', height: '160px', objectFit: 'cover' }}
                        onError={(e) => {
                          (e.target as HTMLElement).style.display = 'none';
                        }} 
                      />
                      <div style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: rec.published !== false ? 'rgba(16, 185, 129, 0.85)' : 'rgba(239, 68, 68, 0.85)', padding: '0.2rem 0.5rem', borderRadius: 'var(--radius-sm)', fontSize: '0.7rem', fontWeight: 600, color: '#fff' }}>
                        {rec.published !== false ? 'Published' : 'Draft'}
                      </div>
                    </div>

                    <h4 style={{ fontWeight: 600, fontSize: '1rem', marginBottom: '0.25rem' }}>{rec.piece}</h4>
                    <p style={{ fontSize: '0.8rem', color: 'var(--accent-gold)', marginBottom: '0.5rem' }}>{rec.composer}</p>
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>{rec.venue} &bull; {rec.date}</p>

                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                      <strong>Performers ({rec.performers.length}):</strong>
                      <ul style={{ paddingLeft: '1.2rem', marginTop: '0.25rem' }}>
                        {rec.performers.map((p, idx) => (
                          <li key={idx}>
                            {p.name} ({p.instrument}) {p.url && <a href={p.url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent-gold)' }}>[link]</a>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', borderTop: '1px solid var(--border-subtle)', paddingTop: '0.75rem' }}>
                    <button onClick={() => toggleRecordingPublished(rec)} className="btn btn-secondary" style={{ flex: 1, padding: '0.4rem', fontSize: '0.75rem' }}>
                      {rec.published !== false ? <EyeOff size={14} /> : <Eye size={14} />}
                      <span>{rec.published !== false ? 'Unpublish' : 'Publish'}</span>
                    </button>
                    <button onClick={() => setEditingRecording(rec)} className="btn btn-secondary" style={{ padding: '0.4rem 0.75rem' }}>
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => deleteRecording(rec.id)} className="btn btn-secondary" style={{ padding: '0.4rem 0.75rem', color: '#f87171' }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB 2: CASE STUDIES */}
        {activeTab === 'casestudies' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h3 style={{ fontSize: '1.35rem', fontWeight: 700 }}>Case Studies & Architecture</h3>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Manage technical writeups, impact highlights, external links, and draft status.</p>
              </div>
              <button
                onClick={() => setEditingCaseStudy({
                  id: `cs_${Date.now()}`,
                  discipline_id: 'cloud',
                  title: '',
                  subtitle: '',
                  category: 'Cloud Security',
                  summary: '',
                  impact: [''],
                  tech_stack: ['Google Cloud'],
                  external_links: {},
                  featured: true,
                  published: true
                })}
                className="btn btn-primary"
              >
                <Plus size={16} />
                <span>Add Case Study</span>
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {caseStudies.map((cs) => (
                <div key={cs.id} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1.5rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '280px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', borderRadius: 'var(--radius-sm)', background: 'var(--bg-tertiary)', color: 'var(--accent-gold)' }}>
                        {cs.category}
                      </span>
                      <span style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', borderRadius: 'var(--radius-sm)', background: cs.published !== false ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)', color: cs.published !== false ? 'var(--accent-emerald)' : '#f87171', fontWeight: 600 }}>
                        {cs.published !== false ? 'Published' : 'Draft'}
                      </span>
                      {cs.featured && (
                        <span style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', borderRadius: 'var(--radius-sm)', background: 'rgba(212, 175, 55, 0.15)', color: 'var(--accent-gold)', fontWeight: 600 }}>
                          Featured
                        </span>
                      )}
                    </div>

                    <h4 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: '0.25rem' }}>{cs.title}</h4>
                    <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>{cs.summary}</p>

                    {cs.impact && cs.impact.length > 0 && (
                      <ul style={{ fontSize: '0.8rem', color: 'var(--text-muted)', paddingLeft: '1.2rem', marginBottom: '0.75rem' }}>
                        {cs.impact.map((imp, idx) => (
                          <li key={idx}>{imp}</li>
                        ))}
                      </ul>
                    )}

                    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                      {cs.tech_stack.map((t) => (
                        <span key={t} style={{ fontSize: '0.72rem', background: 'var(--bg-tertiary)', padding: '0.15rem 0.45rem', borderRadius: 'var(--radius-sm)', color: 'var(--text-muted)' }}>
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => toggleCaseStudyPublished(cs)} className="btn btn-secondary" style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}>
                      {cs.published !== false ? <EyeOff size={14} /> : <Eye size={14} />}
                      <span>{cs.published !== false ? 'Set Draft' : 'Publish'}</span>
                    </button>
                    <button onClick={() => setEditingCaseStudy(cs)} className="btn btn-secondary" style={{ padding: '0.4rem 0.75rem' }}>
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => deleteCaseStudy(cs.id)} className="btn btn-secondary" style={{ padding: '0.4rem 0.75rem', color: '#f87171' }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB 3: CREDENTIALS */}
        {activeTab === 'credentials' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h3 style={{ fontSize: '1.35rem', fontWeight: 700 }}>Background, Education & Honors</h3>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Manage career milestones, certifications, education, and honors.</p>
              </div>
              <button
                onClick={() => setEditingCredential({
                  title: '',
                  institution: '',
                  period: `${new Date().getFullYear()}`,
                  details: '',
                  category: 'experience',
                  published: true
                })}
                className="btn btn-primary"
              >
                <Plus size={16} />
                <span>Add Credential</span>
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1rem' }}>
              {credentials.map((c, idx) => (
                <div key={idx} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '1.25rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                      <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--accent-gold)', fontWeight: 600 }}>{c.category}</span>
                      <span style={{ fontSize: '0.7rem', padding: '0.15rem 0.4rem', borderRadius: 'var(--radius-sm)', background: c.published !== false ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)', color: c.published !== false ? 'var(--accent-emerald)' : '#f87171', fontWeight: 600 }}>
                        {c.published !== false ? 'Published' : 'Draft'}
                      </span>
                    </div>
                    <h4 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '0.25rem' }}>{c.title}</h4>
                    <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{c.institution} &bull; {c.period}</p>
                    {c.details && <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.35rem' }}>{c.details}</p>}
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', borderTop: '1px solid var(--border-subtle)', paddingTop: '0.75rem', marginTop: '1rem' }}>
                    <button onClick={() => toggleCredentialPublished(c)} className="btn btn-secondary" style={{ flex: 1, padding: '0.35rem', fontSize: '0.75rem' }}>
                      {c.published !== false ? <EyeOff size={13} /> : <Eye size={13} />}
                      <span>{c.published !== false ? 'Draft' : 'Publish'}</span>
                    </button>
                    <button onClick={() => setEditingCredential(c)} className="btn btn-secondary" style={{ padding: '0.35rem 0.65rem' }}>
                      <Edit2 size={13} />
                    </button>
                    <button onClick={() => deleteCredential(c.title, c.institution)} className="btn btn-secondary" style={{ padding: '0.35rem 0.65rem', color: '#f87171' }}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB 4: INQUIRIES */}
        {activeTab === 'inquiries' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h3 style={{ fontSize: '1.35rem', fontWeight: 700 }}>Direct Contact Inquiries ({inquiries.length})</h3>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Live messages received through your portfolio contact form.</p>
              </div>

              {inquiries.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <div style={{ display: 'inline-flex', background: 'var(--bg-tertiary)', padding: '0.2rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)', gap: '0.2rem' }}>
                    <button
                      onClick={() => setInquiryFilter('all')}
                      style={{
                        padding: '0.35rem 0.65rem',
                        fontSize: '0.78rem',
                        borderRadius: 'var(--radius-sm)',
                        border: 'none',
                        background: inquiryFilter === 'all' ? 'var(--accent-gold)' : 'transparent',
                        color: inquiryFilter === 'all' ? '#000' : 'var(--text-secondary)',
                        fontWeight: inquiryFilter === 'all' ? 600 : 400,
                        cursor: 'pointer',
                      }}
                    >
                      All ({inquiries.length})
                    </button>
                    <button
                      onClick={() => setInquiryFilter('valid')}
                      style={{
                        padding: '0.35rem 0.65rem',
                        fontSize: '0.78rem',
                        borderRadius: 'var(--radius-sm)',
                        border: 'none',
                        background: inquiryFilter === 'valid' ? 'var(--accent-emerald)' : 'transparent',
                        color: inquiryFilter === 'valid' ? '#000' : 'var(--text-secondary)',
                        fontWeight: inquiryFilter === 'valid' ? 600 : 400,
                        cursor: 'pointer',
                      }}
                    >
                      Inbox ({validCount})
                    </button>
                    <button
                      onClick={() => setInquiryFilter('spam')}
                      style={{
                        padding: '0.35rem 0.65rem',
                        fontSize: '0.78rem',
                        borderRadius: 'var(--radius-sm)',
                        border: 'none',
                        background: inquiryFilter === 'spam' ? '#f87171' : 'transparent',
                        color: inquiryFilter === 'spam' ? '#000' : 'var(--text-secondary)',
                        fontWeight: inquiryFilter === 'spam' ? 600 : 400,
                        cursor: 'pointer',
                      }}
                    >
                      Spam / Filtered ({spamCount})
                    </button>
                  </div>

                  <div style={{ position: 'relative', width: '100%', maxWidth: '260px' }}>
                    <Search size={15} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                      type="text"
                      value={inquirySearch}
                      onChange={(e) => setInquirySearch(e.target.value)}
                      placeholder="Search inquiries, IP..."
                      style={{ width: '100%', padding: '0.5rem 0.75rem 0.5rem 2.25rem', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                    />
                    {inquirySearch && (
                      <button onClick={() => setInquirySearch('')} style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.75rem' }}>
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {inquiries.length === 0 ? (
              <div style={{ padding: '3.5rem 1.5rem', textAlign: 'center', background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>
                <MessageSquare size={32} style={{ margin: '0 auto 0.75rem', opacity: 0.5, color: 'var(--accent-gold)' }} />
                <p style={{ fontWeight: 600, fontSize: '1rem', color: 'var(--text-primary)' }}>No contact inquiries yet</p>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Messages submitted via the contact form will appear here and trigger email alerts.</p>
              </div>
            ) : filteredInquiries.length === 0 ? (
              <div style={{ padding: '2.5rem', textAlign: 'center', background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>
                No inquiries match "{inquirySearch}".
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {filteredInquiries.map((inq) => (
                  <div key={inq.id} style={{ background: 'var(--bg-surface)', border: inq.is_spam ? '1px solid rgba(239, 68, 68, 0.4)' : '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '1.5rem', transition: 'border-color 0.2s' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                          <strong style={{ fontSize: '1.05rem', color: 'var(--text-primary)' }}>{inq.name}</strong>
                          <span style={{ fontSize: '0.82rem', color: 'var(--accent-gold)', background: 'var(--bg-tertiary)', padding: '0.15rem 0.5rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }}>
                            {inq.email}
                          </span>
                          {inq.is_spam && (
                            <span style={{ fontSize: '0.72rem', background: 'rgba(239, 68, 68, 0.15)', color: '#f87171', border: '1px solid rgba(239, 68, 68, 0.3)', padding: '0.15rem 0.5rem', borderRadius: 'var(--radius-sm)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                              <ShieldAlert size={12} />
                              <span>Spam Filtered: {inq.spam_reason || 'Bot Flagged'}</span>
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.92rem', color: 'var(--text-primary)', fontWeight: 600, marginTop: '0.4rem' }}>
                          Subject: {inq.subject}
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{inq.timestamp}</span>
                        {!inq.is_spam && (
                          <a
                            href={`mailto:${inq.email}?subject=Re: ${encodeURIComponent(inq.subject)}&body=${encodeURIComponent(`Hi ${inq.name},\n\nThank you for reaching out through my portfolio!\n\nBest,\nThomas Desrosiers\nhttps://tdesrosi.com`)}`}
                            className="btn btn-primary"
                            style={{ padding: '0.35rem 0.75rem', fontSize: '0.78rem', textDecoration: 'none' }}
                            title="Reply to visitor via your default email client"
                          >
                            <Reply size={13} />
                            <span>Reply</span>
                          </a>
                        )}
                        <button
                          onClick={() => deleteInquiry(inq.id, inq.name)}
                          className="btn btn-secondary"
                          style={{ padding: '0.35rem 0.6rem', fontSize: '0.78rem', color: '#f87171' }}
                          title="Delete inquiry from database"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>

                    <div style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-wrap', background: 'var(--bg-tertiary)', padding: '0.9rem 1.1rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }}>
                      {inq.message}
                    </div>

                    {/* Telemetry & Delivery Footer */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.75rem', paddingTop: '0.65rem', borderTop: '1px solid var(--border-subtle)', fontSize: '0.75rem', color: 'var(--text-muted)', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                        {inq.ip_address && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                            <Globe size={11} color="var(--accent-gold)" />
                            <strong>IP:</strong> {inq.ip_address}
                          </span>
                        )}
                        {inq.user_agent && (
                          <span title={inq.user_agent}>
                            <strong>Client:</strong> {inq.user_agent.length > 50 ? inq.user_agent.slice(0, 50) + '...' : inq.user_agent}
                          </span>
                        )}
                      </div>
                      <span style={{ color: inq.is_spam ? '#f87171' : 'var(--accent-emerald)', fontWeight: 500 }}>
                        {inq.is_spam ? '🚫 Inbox notification blocked' : '✓ Delivered to inbox'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* EDIT RECORDING MODAL */}
      {editingRecording && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1.5rem' }}>
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', maxWidth: '680px', width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>Edit Performance Recording</h3>
              <button onClick={() => setEditingRecording(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Piece Title</label>
                <input
                  type="text"
                  value={editingRecording.piece}
                  onChange={(e) => setEditingRecording({ ...editingRecording, piece: e.target.value, title: `${editingRecording.composer ? editingRecording.composer + ' — ' : ''}${e.target.value}` })}
                  placeholder="e.g. Clarinet Quintet in B minor, Op. 115"
                  style={{ width: '100%', padding: '0.6rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Composer</label>
                  <input
                    type="text"
                    value={editingRecording.composer}
                    onChange={(e) => setEditingRecording({ ...editingRecording, composer: e.target.value, title: `${e.target.value ? e.target.value + ' — ' : ''}${editingRecording.piece}` })}
                    placeholder="e.g. Johannes Brahms (1833–1897)"
                    style={{ width: '100%', padding: '0.6rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Movement / Section</label>
                  <input
                    type="text"
                    value={editingRecording.movement || ''}
                    onChange={(e) => setEditingRecording({ ...editingRecording, movement: e.target.value })}
                    placeholder="e.g. II. Adagio"
                    style={{ width: '100%', padding: '0.6rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>YouTube URL or Video ID</label>
                <input
                  type="text"
                  value={editingRecording.youtube_url}
                  onChange={(e) => {
                    const url = e.target.value;
                    let ytid = url;
                    if (url.includes('youtu.be/')) ytid = url.split('youtu.be/')[1]?.split('?')[0];
                    else if (url.includes('v=')) ytid = url.split('v=')[1]?.split('&')[0];
                    setEditingRecording({ ...editingRecording, youtube_url: url, youtube_id: ytid });
                  }}
                  placeholder="https://youtu.be/DLX7vikFCcE or DLX7vikFCcE"
                  style={{ width: '100%', padding: '0.6rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)' }}
                />
              </div>

              {editingRecording.youtube_id && (
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', background: 'var(--bg-tertiary)', padding: '0.75rem', borderRadius: 'var(--radius-sm)' }}>
                  <img 
                    src={`https://i.ytimg.com/vi/${editingRecording.youtube_id}/hqdefault.jpg`} 
                    alt="Preview" 
                    style={{ width: '120px', height: '68px', objectFit: 'cover', borderRadius: 'var(--radius-sm)' }}
                  />
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    <div><strong>Detected ID:</strong> {editingRecording.youtube_id}</div>
                    <a href={`https://youtu.be/${editingRecording.youtube_id}`} target="_blank" rel="noreferrer" style={{ color: 'var(--accent-gold)', display: 'inline-flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.25rem' }}>
                      <ExternalLink size={12} /> View on YouTube
                    </a>
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Recording / Performance Date</label>
                  <input
                    type="text"
                    value={editingRecording.date || ''}
                    onChange={(e) => setEditingRecording({ ...editingRecording, date: e.target.value })}
                    placeholder="e.g. August 2026 or August 15, 2026"
                    style={{ width: '100%', padding: '0.6rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Instrumentation</label>
                  <input
                    type="text"
                    value={editingRecording.instrumentation || ''}
                    onChange={(e) => setEditingRecording({ ...editingRecording, instrumentation: e.target.value })}
                    placeholder="e.g. Clarinet and Piano or Solo Clarinet"
                    style={{ width: '100%', padding: '0.6rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Venue / Platform Location</label>
                  <input
                    type="text"
                    value={editingRecording.venue}
                    onChange={(e) => setEditingRecording({ ...editingRecording, venue: e.target.value })}
                    placeholder="e.g. Unitarian Universalist Congregation of Atlanta"
                    style={{ width: '100%', padding: '0.6rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Category</label>
                  <input
                    type="text"
                    value={editingRecording.category || ''}
                    onChange={(e) => setEditingRecording({ ...editingRecording, category: e.target.value })}
                    placeholder="e.g. Live Classical Recital or Studio Recording"
                    style={{ width: '100%', padding: '0.6rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Event / Series</label>
                <input
                  type="text"
                  value={editingRecording.event || ''}
                  onChange={(e) => setEditingRecording({ ...editingRecording, event: e.target.value })}
                  placeholder="e.g. A Return to Performing: Clarinet Recital"
                  style={{ width: '100%', padding: '0.6rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Description / Notes</label>
                <textarea
                  rows={2}
                  value={editingRecording.description || ''}
                  onChange={(e) => setEditingRecording({ ...editingRecording, description: e.target.value })}
                  placeholder="Brief description of the performance or piece..."
                  style={{ width: '100%', padding: '0.6rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)', resize: 'vertical' }}
                />
              </div>

              {/* Streaming Links Manager */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Streaming & External Links (Apple Music, Songlink, etc.)</label>
                  <button
                    type="button"
                    onClick={() => {
                      const links = { ...(editingRecording.streaming_links || {}) };
                      links['Apple Music'] = '';
                      setEditingRecording({ ...editingRecording, streaming_links: links });
                    }}
                    className="btn btn-secondary"
                    style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                  >
                    <Plus size={12} /> Add Link
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {Object.entries(editingRecording.streaming_links || {}).map(([platform, linkUrl], idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '150px 1fr auto', gap: '0.5rem', alignItems: 'center', background: 'var(--bg-tertiary)', padding: '0.5rem', borderRadius: 'var(--radius-sm)' }}>
                      <input
                        type="text"
                        placeholder="Platform (e.g. Apple Music)"
                        value={platform}
                        onChange={(e) => {
                          const entries = Object.entries(editingRecording.streaming_links || {});
                          const newObj: Record<string, string> = {};
                          entries.forEach(([k, v], i) => {
                            if (i === idx) newObj[e.target.value] = v;
                            else newObj[k] = v;
                          });
                          setEditingRecording({ ...editingRecording, streaming_links: newObj });
                        }}
                        style={{ padding: '0.4rem', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)', fontSize: '0.8rem' }}
                      />
                      <input
                        type="text"
                        placeholder="https://..."
                        value={linkUrl}
                        onChange={(e) => {
                          const links = { ...(editingRecording.streaming_links || {}) };
                          links[platform] = e.target.value;
                          setEditingRecording({ ...editingRecording, streaming_links: links });
                        }}
                        style={{ padding: '0.4rem', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)', fontSize: '0.8rem' }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const links = { ...(editingRecording.streaming_links || {}) };
                          delete links[platform];
                          setEditingRecording({ ...editingRecording, streaming_links: links });
                        }}
                        style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', padding: '0.25rem' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Performers Manager */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Collaborators & Performers</label>
                  <button
                    type="button"
                    onClick={() => {
                      const newPerfs = [...editingRecording.performers, { name: '', instrument: '', affiliation: '', url: '' }];
                      setEditingRecording({ ...editingRecording, performers: newPerfs });
                    }}
                    className="btn btn-secondary"
                    style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                  >
                    <Plus size={12} /> Add Performer
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {editingRecording.performers.map((p, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '0.5rem', alignItems: 'center', background: 'var(--bg-tertiary)', padding: '0.5rem', borderRadius: 'var(--radius-sm)' }}>
                      <input
                        type="text"
                        placeholder="Performer Name"
                        value={p.name}
                        onChange={(e) => {
                          const updated = [...editingRecording.performers];
                          updated[idx].name = e.target.value;
                          setEditingRecording({ ...editingRecording, performers: updated });
                        }}
                        style={{ padding: '0.4rem', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)', fontSize: '0.8rem' }}
                      />
                      <input
                        type="text"
                        placeholder="Instrument (e.g. Piano)"
                        value={p.instrument}
                        onChange={(e) => {
                          const updated = [...editingRecording.performers];
                          updated[idx].instrument = e.target.value;
                          setEditingRecording({ ...editingRecording, performers: updated });
                        }}
                        style={{ padding: '0.4rem', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)', fontSize: '0.8rem' }}
                      />
                      <input
                        type="text"
                        placeholder="Website / Profile URL"
                        value={p.url || ''}
                        onChange={(e) => {
                          const updated = [...editingRecording.performers];
                          updated[idx].url = e.target.value;
                          setEditingRecording({ ...editingRecording, performers: updated });
                        }}
                        style={{ padding: '0.4rem', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)', fontSize: '0.8rem' }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const updated = editingRecording.performers.filter((_, i) => i !== idx);
                          setEditingRecording({ ...editingRecording, performers: updated });
                        }}
                        style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', padding: '0.25rem' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                <input
                  type="checkbox"
                  id="rec-published-toggle"
                  checked={editingRecording.published !== false}
                  onChange={(e) => setEditingRecording({ ...editingRecording, published: e.target.checked })}
                />
                <label htmlFor="rec-published-toggle" style={{ fontSize: '0.85rem', cursor: 'pointer' }}>
                  Publish on live site (uncheck to save as draft)
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '2rem' }}>
              <button onClick={() => setEditingRecording(null)} className="btn btn-secondary">Cancel</button>
              <button onClick={() => saveRecording(editingRecording)} className="btn btn-primary">
                <Save size={16} />
                <span>Save Recording</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT CASE STUDY MODAL */}
      {editingCaseStudy && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1.5rem' }}>
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', maxWidth: '720px', width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>Edit Case Study</h3>
              <button onClick={() => setEditingCaseStudy(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Project / Work Title</label>
                <input
                  type="text"
                  value={editingCaseStudy.title}
                  onChange={(e) => setEditingCaseStudy({ ...editingCaseStudy, title: e.target.value })}
                  placeholder="e.g. Enterprise Security, AI Security & Zero-Trust"
                  style={{ width: '100%', padding: '0.6rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Discipline</label>
                  <select
                    value={editingCaseStudy.discipline_id}
                    onChange={(e) => setEditingCaseStudy({ ...editingCaseStudy, discipline_id: e.target.value })}
                    style={{ width: '100%', padding: '0.6rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)' }}
                  >
                    <option value="cloud">Cloud & Security Systems</option>
                    <option value="music">Classical Music & Arts Leadership</option>
                    <option value="maker">Hardware & Creative Technology</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Category Tag</label>
                  <input
                    type="text"
                    value={editingCaseStudy.category}
                    onChange={(e) => setEditingCaseStudy({ ...editingCaseStudy, category: e.target.value })}
                    placeholder="e.g. Security Architecture"
                    style={{ width: '100%', padding: '0.6rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Subtitle</label>
                <input
                  type="text"
                  value={editingCaseStudy.subtitle}
                  onChange={(e) => setEditingCaseStudy({ ...editingCaseStudy, subtitle: e.target.value })}
                  placeholder="e.g. Zero-Trust Architecture & Policy-as-Code"
                  style={{ width: '100%', padding: '0.6rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Summary</label>
                <textarea
                  rows={3}
                  value={editingCaseStudy.summary}
                  onChange={(e) => setEditingCaseStudy({ ...editingCaseStudy, summary: e.target.value })}
                  placeholder="Overview of the project impact and mission..."
                  style={{ width: '100%', padding: '0.6rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)', resize: 'vertical' }}
                />
              </div>

              {/* Dynamic Impact Bullets */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Key Impact Highlights</label>
                  <button
                    type="button"
                    onClick={() => {
                      const updated = [...(editingCaseStudy.impact || []), ''];
                      setEditingCaseStudy({ ...editingCaseStudy, impact: updated });
                    }}
                    className="btn btn-secondary"
                    style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                  >
                    <Plus size={12} /> Add Impact Bullet
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {(editingCaseStudy.impact || []).map((imp, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <input
                        type="text"
                        value={imp}
                        placeholder="e.g. Scaled active adoption from 85 to 2,300+ engineers..."
                        onChange={(e) => {
                          const updated = [...editingCaseStudy.impact];
                          updated[idx] = e.target.value;
                          setEditingCaseStudy({ ...editingCaseStudy, impact: updated });
                        }}
                        style={{ flex: 1, padding: '0.5rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)', fontSize: '0.85rem' }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const updated = editingCaseStudy.impact.filter((_, i) => i !== idx);
                          setEditingCaseStudy({ ...editingCaseStudy, impact: updated });
                        }}
                        style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', padding: '0.25rem' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* External Links Map */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>External Links (e.g. GitHub, Website, Docs)</label>
                  <button
                    type="button"
                    onClick={() => {
                      const links = { ...(editingCaseStudy.external_links || {}), '': '' };
                      setEditingCaseStudy({ ...editingCaseStudy, external_links: links });
                    }}
                    className="btn btn-secondary"
                    style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                  >
                    <Plus size={12} /> Add Link
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {Object.entries(editingCaseStudy.external_links || {}).map(([key, val], idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: '0.5rem', alignItems: 'center' }}>
                      <input
                        type="text"
                        placeholder="Label (e.g. GitHub PR)"
                        defaultValue={key}
                        onBlur={(e) => {
                          const newKey = e.target.value;
                          if (newKey !== key) {
                            const newLinks: Record<string, string> = {};
                            for (const [k, v] of Object.entries(editingCaseStudy.external_links || {})) {
                              if (k === key) newLinks[newKey] = val;
                              else newLinks[k] = v;
                            }
                            setEditingCaseStudy({ ...editingCaseStudy, external_links: newLinks });
                          }
                        }}
                        style={{ padding: '0.4rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)', fontSize: '0.8rem' }}
                      />
                      <input
                        type="text"
                        placeholder="https://..."
                        value={val}
                        onChange={(e) => {
                          const newLinks = { ...(editingCaseStudy.external_links || {}), [key]: e.target.value };
                          setEditingCaseStudy({ ...editingCaseStudy, external_links: newLinks });
                        }}
                        style={{ padding: '0.4rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)', fontSize: '0.8rem' }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newLinks = { ...(editingCaseStudy.external_links || {}) };
                          delete newLinks[key];
                          setEditingCaseStudy({ ...editingCaseStudy, external_links: newLinks });
                        }}
                        style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', padding: '0.25rem' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Tech Stack (comma separated)</label>
                <input
                  type="text"
                  value={editingCaseStudy.tech_stack.join(', ')}
                  onChange={(e) => setEditingCaseStudy({ ...editingCaseStudy, tech_stack: e.target.value.split(',').map((t) => t.trim()).filter(Boolean) })}
                  placeholder="Terraform, Go, Google Cloud, Zero-Trust"
                  style={{ width: '100%', padding: '0.6rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    id="cs-published-toggle"
                    checked={editingCaseStudy.published !== false}
                    onChange={(e) => setEditingCaseStudy({ ...editingCaseStudy, published: e.target.checked })}
                  />
                  <label htmlFor="cs-published-toggle" style={{ fontSize: '0.85rem', cursor: 'pointer' }}>
                    Published on live site
                  </label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    id="cs-featured-toggle"
                    checked={editingCaseStudy.featured !== false}
                    onChange={(e) => setEditingCaseStudy({ ...editingCaseStudy, featured: e.target.checked })}
                  />
                  <label htmlFor="cs-featured-toggle" style={{ fontSize: '0.85rem', cursor: 'pointer' }}>
                    Featured on homepage
                  </label>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '2rem' }}>
              <button onClick={() => setEditingCaseStudy(null)} className="btn btn-secondary">Cancel</button>
              <button onClick={() => saveCaseStudy(editingCaseStudy)} className="btn btn-primary">
                <Save size={16} />
                <span>Save Case Study</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT CREDENTIAL MODAL */}
      {editingCredential && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1.5rem' }}>
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', maxWidth: '580px', width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>Edit Credential & Milestone</h3>
              <button onClick={() => setEditingCredential(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Title / Role / Award</label>
                <input
                  type="text"
                  value={editingCredential.title}
                  onChange={(e) => setEditingCredential({ ...editingCredential, title: e.target.value })}
                  placeholder="e.g. Strategic Cloud Engineer"
                  style={{ width: '100%', padding: '0.6rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Institution / Organization</label>
                  <input
                    type="text"
                    value={editingCredential.institution}
                    onChange={(e) => setEditingCredential({ ...editingCredential, institution: e.target.value })}
                    placeholder="e.g. Google Cloud"
                    style={{ width: '100%', padding: '0.6rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Period / Year</label>
                  <input
                    type="text"
                    value={editingCredential.period}
                    onChange={(e) => setEditingCredential({ ...editingCredential, period: e.target.value })}
                    placeholder="e.g. 2022 – Present"
                    style={{ width: '100%', padding: '0.6rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Category</label>
                <select
                  value={editingCredential.category}
                  onChange={(e) => setEditingCredential({ ...editingCredential, category: e.target.value as any })}
                  style={{ width: '100%', padding: '0.6rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)' }}
                >
                  <option value="experience">Professional Experience</option>
                  <option value="education">Education & Degrees</option>
                  <option value="certification">Industry Certifications</option>
                  <option value="award">Honors & Awards</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Details & Scope Description</label>
                <textarea
                  rows={3}
                  value={editingCredential.details || ''}
                  onChange={(e) => setEditingCredential({ ...editingCredential, details: e.target.value })}
                  placeholder="Enterprise security, developer tooling, and Zero-Trust platform leadership..."
                  style={{ width: '100%', padding: '0.6rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', color: '#fff', borderRadius: 'var(--radius-sm)', resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                <input
                  type="checkbox"
                  id="cred-published-toggle"
                  checked={editingCredential.published !== false}
                  onChange={(e) => setEditingCredential({ ...editingCredential, published: e.target.checked })}
                />
                <label htmlFor="cred-published-toggle" style={{ fontSize: '0.85rem', cursor: 'pointer' }}>
                  Publish on live site (uncheck to save as draft)
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '2rem' }}>
              <button onClick={() => setEditingCredential(null)} className="btn btn-secondary">Cancel</button>
              <button onClick={() => saveCredential(editingCredential)} className="btn btn-primary">
                <Save size={16} />
                <span>Save Credential</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

